import { Sine, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import * as SoundEffect from "../Data/SoundEffect";
import * as Topics from "../Data/Topics";
import { RadialFillFilter } from "../Filters/RadialFillFilter";
import { PaintableHitArea } from "../GameObjects/PaintableHitArea";

export class PaintableLayer extends PIXI.Container implements HLF.Input.IFocusable {
    private _sprite: PIXI.Sprite;
    private _filter: RadialFillFilter;
    private _paletteColour: number;
    private _prevColour: HLF.Colour;
    private _currColour: HLF.Colour;
    private _eventData: PIXI.interaction.InteractionData | undefined;
    private _tween: TweenLite | undefined;
    private _animPerc: number;
    private _isAnimating: boolean;
    private _inputEnabled: boolean;
    private _paintedCallback: () => void;
    private _touchPos: PIXI.Point;
    private _layerIndex: number;
    private _partner: PaintableLayer | undefined;

    constructor(pAsset: string, pSheet: string, pCallback: () => void, pMap?: number[][], pLayerIndex?: number) {
        super();
        this._paletteColour = 0xFFFFFF;
        this._prevColour = new HLF.Colour(255, 255, 255);
        this._currColour = new HLF.Colour(255, 255, 255);
        this._isAnimating = false;
        this._inputEnabled = true;
        this._animPerc = 0;
        this._tween = undefined;
        this._paintedCallback = pCallback;
        this._touchPos = new PIXI.Point(0, 0);
        this._layerIndex = parseInt(pAsset.substr(pAsset.length - 2, 2), 10);

        this._sprite = HLF.PixiUtils.makeSprite(pAsset, pSheet);
        this.addChild(this._sprite);

        this._filter = new RadialFillFilter();
        this._filter.apply = function(filterManager: any, input: any, output: any, clear: any) {
            this.uniforms.dimensions[0] = input.sourceFrame.width;
            this.uniforms.dimensions[1] = input.sourceFrame.height;

            filterManager.applyFilter(this, input, output, clear);
        };
        this._filter.uniforms.uColourCurr = this._currColour.toWebGL();
        this._filter.uniforms.uColourPrev = this._prevColour.toWebGL();

        if (pMap !== undefined && pLayerIndex !== undefined) {
            this.hitArea = new PaintableHitArea(this._sprite, pMap, pLayerIndex, 2);
            this.interactive = true;
            this.buttonMode = true;

            this.on(HLF.Input.Events.POINTER_DOWN, this.onPointerDown);
            this.on(HLF.Input.Events.POINTER_UP, this.onPointerUp);
        }
    }

    public set paletteColour(pValue: number) {
        this._paletteColour = pValue;
    }

    public get paintedColour(): number {
        return this._currColour.toHex();
    }

    public get animPerc(): number {
        return this._animPerc;
    }

    public set animPerc(pValue: number) {
        this._animPerc = pValue;
    }

    public get touchPos(): PIXI.Point {
        return this._touchPos;
    }

    public set partner(pValue: PaintableLayer) {
        this._partner = pValue;
    }

    public get sprite(): PIXI.Sprite {
        return this._sprite;
    }

    public onFocusBegin(): void {
        PubSub.publishSync(Topics.KEYBOARD_FOCUS_BEGIN, this);
        const colour: HLF.Colour = new HLF.Colour(this._paletteColour);
        this.preview(colour);
    }

    public onFocusActivated(): void {
        if (this.interactive) {
            this._touchPos.set(0.5);
            this.paint(this._paletteColour, 0.5);
        }
    }

    public onFocusEnd(): void {
        PubSub.publishSync(Topics.KEYBOARD_FOCUS_END, this);
        this.paint(this.paintedColour, 0.5, 0.5, true);
    }

    public getFocusPosition(): PIXI.Point {
        const global: PIXI.Point = this.getGlobalPosition();
        global.y += this._layerIndex;
        return global;
    }

    public getFocusSize(): PIXI.Point {
        return new PIXI.Point(this.width, this.height);
    }

    public fill(): void {
        this._sprite.tint = this._paletteColour;
    }

    public paint(pColour: number, pX: number = 0.5, pY: number = pX, pInstantly: boolean = false,
                 pFirstCall: boolean = true, pPreventCallback: boolean = false, pForce: boolean = false): void {
        if (this._isAnimating === false || pForce === true) {
            this._prevColour = this._currColour;
            this._currColour = new HLF.Colour(pColour);
            this._isAnimating = true;
            this._animPerc = 0;
            this._filters = [ this._filter ];
            this._sprite.tint = 0xFFFFFF;

            this._filter.uniforms.uColourCurr = this._currColour.toWebGL();
            this._filter.uniforms.uColourPrev = this._prevColour.toWebGL();
            this._filter.uniforms.uPerc = this._animPerc;
            this._filter.uniforms.uOrigin = [ pX, pY ];

            if (this._tween !== undefined) {
                this._tween.kill();
            }
            this._tween = TweenLite.to(this, pInstantly ? 0 : 1.5, {
                animPerc: 1,
                ease: Sine.easeOut,
                onUpdate: this.onFilterAnimUpdate.bind(this),
                onComplete: this.onFilterAnimComplete.bind(this),
            });

            if (pFirstCall) {
                if (pPreventCallback === false && this._paintedCallback !== undefined) {
                    this._paintedCallback();
                }
                if (this._partner !== undefined) {
                    this._partner.paint(pColour, pX, pY, pInstantly, false, pPreventCallback, pForce);
                }
                if (pInstantly === false) {
                    PubSub.publishSync(Topics.PAINTABLE_LAYER_PAINTED, undefined);
                    PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(
                        HLF.ArrayUtils.random(SoundEffect.WETPAINT),
                        1, false, HLF.AudioCategory.SFX),
                    );
                }
            }
        }
    }

    public preview(pColour: HLF.Colour, pFirstCall: boolean = true): void {
        if (this._tween !== undefined && this._isAnimating) {
            this._tween.kill();
            this.onFilterAnimComplete();
        }
        const colour: HLF.Colour = HLF.Colour.lerp(pColour, HLF.Colour.WHITE, 0.5);
        this._sprite.tint = colour.toHex();

        if (pFirstCall === true && this._partner !== undefined) {
            this._partner.preview(pColour, false);
        }
    }

    public setInputEnabled(pEnabled: boolean): void {
        this._inputEnabled = pEnabled;
        this.buttonMode = this._inputEnabled;
    }

    protected onPointerDown(pEvent: PIXI.interaction.InteractionEvent): void {
        if (this._inputEnabled) {
            this._eventData = pEvent.data;
        }
    }

    protected onPointerUp(): void {
        if (this._inputEnabled) {
            if (this._eventData !== undefined) {
                const localPos: PIXI.Point = this._eventData.getLocalPosition(this.parent);
                this._touchPos.set(
                    this._touchPos.x = (localPos.x / this.width) + 0.5,
                    this._touchPos.y = (localPos.y / this.height) + 0.5,
                );
                this.paint(this._paletteColour, this._touchPos.x, this._touchPos.y);
                this._eventData = undefined;
            }
        }
        else {
            this._eventData = undefined;
        }
    }

    private onFilterAnimUpdate(): void {
        this._filter.uniforms.uPerc = this._animPerc;
    }

    private onFilterAnimComplete(): void {
        this._filters = null;
        this._sprite.tint = this._currColour.toHex();
        this._isAnimating = false;
    }
}
